import apiClient from './client.js'

// Every path below was verified against /openapi.json on the live service.
// Method names are unchanged from the original so existing pages keep working —
// several of the PATHS were wrong and silently 404'd (gate, qr, class booking,
// report export, enrolment list).

// ─── Auth ────────────────────────────────────────────────────────────────────
export const auth = {
  login: (username, password) =>
    apiClient.post('/auth/login', { username, password }),

  getMe: () =>
    apiClient.get('/auth/me'),

  changePassword: (current_password, new_password) =>
    apiClient.post('/auth/change-password', { current_password, new_password }),
}

// ─── Staff ───────────────────────────────────────────────────────────────────
export const staff = {
  list: (params = {}) =>
    apiClient.get('/auth/staff', { params }),

  create: (data) =>
    apiClient.post('/auth/staff', data),

  update: (id, data) =>
    apiClient.put(`/auth/staff/${id}`, data),

  deactivate: (id) =>
    apiClient.post(`/auth/staff/${id}/deactivate`),
}

// ─── Members ─────────────────────────────────────────────────────────────────
export const members = {
  list: (params = {}) =>
    apiClient.get('/members', { params }),

  get: (id) =>
    apiClient.get(`/members/${id}`),

  create: (data) =>
    apiClient.post('/members', data),

  update: (id, data) =>
    apiClient.put(`/members/${id}`, data),

  remove: (id) =>
    apiClient.delete(`/members/${id}`),

  block: (id) =>
    apiClient.post(`/members/${id}/block`),

  unblock: (id) =>
    apiClient.post(`/members/${id}/unblock`),

  // was /qr — the route is /qr-code
  getQrCode: (id) =>
    apiClient.get(`/members/${id}/qr-code`),

  regenerateQr: (id) =>
    apiClient.post(`/members/${id}/regenerate-qr`),

  getEnrollments: (id) =>
    apiClient.get(`/members/${id}/enrollments`),

  getCheckins: (id, params = {}) =>
    apiClient.get(`/members/${id}/checkins`, { params }),
}

// ─── Plans ───────────────────────────────────────────────────────────────────
export const plans = {
  list: () =>
    apiClient.get('/plans'),

  listPublic: () =>
    apiClient.get('/plans/public'),

  create: (data) =>
    apiClient.post('/plans', data),

  update: (id, data) =>
    apiClient.put(`/plans/${id}`, data),

  remove: (id) =>
    apiClient.delete(`/plans/${id}`),
}

// ─── Enrollments ─────────────────────────────────────────────────────────────
export const enrollments = {
  // there is no GET /enrollments — the list route is /enrollments/active
  list: (params = {}) =>
    apiClient.get('/enrollments/active', { params }),

  get: (id) =>
    apiClient.get(`/enrollments/${id}`),

  create: (data) =>
    apiClient.post('/enrollments', data),

  pause: (id, data) =>
    apiClient.post(`/enrollments/${id}/pause`, data),

  cancel: (id, data) =>
    apiClient.post(`/enrollments/${id}/cancel`, data),

  reactivate: (id) =>
    apiClient.post(`/enrollments/${id}/reactivate`),
}

// ─── Check-ins ───────────────────────────────────────────────────────────────
export const checkins = {
  list: (params = {}) =>
    apiClient.get('/checkins', { params }),

  create: (data) =>
    apiClient.post('/checkins', data),

  checkout: (id) =>
    apiClient.post(`/checkins/${id}/checkout`),

  remove: (id) =>
    apiClient.delete(`/checkins/${id}`),
}

// ─── Classes ─────────────────────────────────────────────────────────────────
export const classes = {
  list: (params = {}) =>
    apiClient.get('/classes', { params }),

  get: (id) =>
    apiClient.get(`/classes/${id}`),

  create: (data) =>
    apiClient.post('/classes', data),

  update: (id, data) =>
    apiClient.put(`/classes/${id}`, data),

  remove: (id) =>
    apiClient.delete(`/classes/${id}`),

  // was /bookings — the route is /book
  book: (classId, data) =>
    apiClient.post(`/classes/${classId}/book`, data),

  cancelBooking: (classId, bookingId) =>
    apiClient.delete(`/classes/${classId}/bookings/${bookingId}`),

  getAttendees: (classId) =>
    apiClient.get(`/classes/${classId}/attendees`),

  checkInBooking: (classId, bookingId) =>
    apiClient.post(`/classes/${classId}/checkin/${bookingId}`),

  // was /bookings/{id}/no-show — the route is /no-show/{id}
  markNoShow: (classId, bookingId) =>
    apiClient.post(`/classes/${classId}/no-show/${bookingId}`),

  noShows: (params = {}) =>
    apiClient.get('/classes/no-shows', { params }),
}

// ─── Appointments ────────────────────────────────────────────────────────────
export const appointments = {
  list: (params = {}) =>
    apiClient.get('/appointments', { params }),

  get: (id) =>
    apiClient.get(`/appointments/${id}`),

  create: (data) =>
    apiClient.post('/appointments', data),

  update: (id, data) =>
    apiClient.put(`/appointments/${id}`, data),

  complete: (id, staff_notes = null) =>
    apiClient.post(`/appointments/${id}/complete`, { staff_notes }),

  noShow: (id, staff_notes = null) =>
    apiClient.post(`/appointments/${id}/no-show`, { staff_notes }),
}

// ─── Reports ─────────────────────────────────────────────────────────────────
export const reports = {
  dashboard: () =>
    apiClient.get('/reports/dashboard'),

  revenue: (from, to) =>
    apiClient.get('/reports/revenue', { params: { from, to } }),

  attendance: (from, to) =>
    apiClient.get('/reports/attendance', { params: { from, to } }),

  members: (from, to) =>
    apiClient.get('/reports/members', { params: { from, to } }),

  payroll: (from, to) =>
    apiClient.get('/reports/payroll', { params: { from, to } }),

  celebrations: () =>
    apiClient.get('/reports/celebrations'),

  failedPayments: () =>
    apiClient.get('/reports/failed-payments'),

  // was /reports/export
  exportCsv: (from, to) =>
    apiClient.get('/reports/export/transactions', {
      params: { from, to },
      responseType: 'blob',
    }),
}

// ─── Billing ─────────────────────────────────────────────────────────────────
export const billing = {
  summary: () =>
    apiClient.get('/billing/summary'),

  memberSummary: (memberId) =>
    apiClient.get(`/billing/members/${memberId}/summary`),

  listInvoices: (params = {}) =>
    apiClient.get('/billing/invoices', { params }),

  getInvoice: (id) =>
    apiClient.get(`/billing/invoices/${id}`),

  createInvoice: (data) =>
    apiClient.post('/billing/invoices', data),

  collectInvoice: (id) =>
    apiClient.post(`/billing/invoices/${id}/collect`),

  retryInvoice: (id) =>
    apiClient.post(`/billing/invoices/${id}/retry`),

  voidInvoice: (id) =>
    apiClient.post(`/billing/invoices/${id}/void`),

  refundPayment: (paymentId, amount, reason = null) =>
    apiClient.post(`/billing/payments/${paymentId}/refund`, { amount, reason }),

  failed: (params = {}) =>
    apiClient.get('/billing/failed', { params }),

  charge: (data) =>
    apiClient.post('/billing/charge', data),

  nightlyRetry: () =>
    apiClient.post('/billing/internal/nightly-retry'),
}

// ─── Gate ────────────────────────────────────────────────────────────────────
export const gate = {
  // was /gate/access-log
  list: (params = {}) =>
    apiClient.get('/gate/logs', { params }),

  denied: (params = {}) =>
    apiClient.get('/gate/logs/denied', { params }),

  // was POST /gate/access
  scan: (token, gate_id) =>
    apiClient.post('/gate/scan', { token, gate_id }),

  // kept as an alias so the existing GatePage import keeps working
  grant: (data) =>
    apiClient.post('/gate/scan', data),
}

// ─── Products / POS ──────────────────────────────────────────────────────────
export const products = {
  list: (params = {}) =>
    apiClient.get('/products', { params }),

  get: (id) =>
    apiClient.get(`/products/${id}`),

  create: (data) =>
    apiClient.post('/products', data),

  update: (id, data) =>
    apiClient.put(`/products/${id}`, data),

  remove: (id) =>
    apiClient.delete(`/products/${id}`),

  adjustInventory: (id, data) =>
    apiClient.post(`/products/${id}/inventory`, data),

  // Selling
  sell: (data) =>
    apiClient.post('/products/sales', data),

  sales: (params = {}) =>
    apiClient.get('/products/sales', { params }),

  salesSummary: (params = {}) =>
    apiClient.get('/products/sales/summary', { params }),

  voidSale: (saleId, restock = true) =>
    apiClient.delete(`/products/sales/${saleId}`, { params: { restock } }),
}

// ─── Leads ───────────────────────────────────────────────────────────────────
export const leads = {
  list: (params = {}) =>
    apiClient.get('/leads', { params }),

  stats: () =>
    apiClient.get('/leads/stats'),

  get: (id) =>
    apiClient.get(`/leads/${id}`),

  create: (data) =>
    apiClient.post('/leads', data),

  update: (id, data) =>
    apiClient.put(`/leads/${id}`, data),

  convert: (id, data = {}) =>
    apiClient.post(`/leads/${id}/convert`, data),

  remove: (id) =>
    apiClient.delete(`/leads/${id}`),
}

// ─── Waivers ─────────────────────────────────────────────────────────────────
export const waivers = {
  list: (params = {}) =>
    apiClient.get('/waivers', { params }),

  get: (id) =>
    apiClient.get(`/waivers/${id}`),

  create: (data) =>
    apiClient.post('/waivers', data),

  update: (id, data) =>
    apiClient.put(`/waivers/${id}`, data),

  activate: (id) =>
    apiClient.post(`/waivers/${id}/activate`),

  remove: (id) =>
    apiClient.delete(`/waivers/${id}`),

  compliance: () =>
    apiClient.get('/waivers/compliance'),

  unsigned: (params = {}) =>
    apiClient.get('/waivers/unsigned', { params }),

  signatures: (params = {}) =>
    apiClient.get('/waivers/signatures', { params }),

  waiverSignatures: (id) =>
    apiClient.get(`/waivers/${id}/signatures`),
}
